import { createContext, ReactNode, useMemo } from 'react';
import useElementPositionAndSize from '../shared/hooks/useElementPositionAndSize';

type ScreenSize = 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge';

const screenSizeMinLimiters: Record<ScreenSize, number> = {
  extraSmall: 0,
  small: 1080,
  medium: 1366,
  large: 1600,
  extraLarge: 1920,
};

function getScreenSize(width: number): ScreenSize {
  if (width >= screenSizeMinLimiters.extraLarge)
    return 'extraLarge';

  if (width >= screenSizeMinLimiters.large)
    return 'large';

  if (width >= screenSizeMinLimiters.medium)
    return 'medium';

  if (width >= screenSizeMinLimiters.small)
    return 'small';

  return 'extraSmall';
}

interface ContextValue {
  width: number;
  height: number;
  screenSize: ScreenSize;
  /**
   * Is the screen width at least 1080px or not.
   */
  isSmallOrLarger: boolean;
  /**
   * Is the screen width at least 1366px or not.
   */
  isMediumOrLarger: boolean;
  /**
   * Is the screen width at least 1600px or not.
   */
  isLargeOrLarger: boolean;
  /**
   * Is the screen width at least 1920px or not.
   */
  isExtraLarge: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const PageSizeContext = createContext<ContextValue>(undefined!);

function PageSizeProvider({ children }: { children: ReactNode }): JSX.Element {

  const { width, height } = useElementPositionAndSize(document.body);

  const value = useMemo<ContextValue>(
    () => ({
      width,
      height,
      screenSize: getScreenSize(width),
      isSmallOrLarger: width >= screenSizeMinLimiters.small,
      isMediumOrLarger: width >= screenSizeMinLimiters.medium,
      isLargeOrLarger: width >= screenSizeMinLimiters.large,
      isExtraLarge: width >= screenSizeMinLimiters.extraLarge,
    }),
    [height, width],
  );

  return (
    <PageSizeContext.Provider value={value}>
      {children}
    </PageSizeContext.Provider>
  );
}

export default PageSizeProvider;